import { useMemo } from 'react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  VersionDrawer,
  type VersionEntry,
} from '@shared/components/VersionDrawer';
import { pagesApi } from '@api/pages';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';

interface Props {
  open: boolean;
  pageId: string;
  currentBody: string;
  onClose: () => void;
}

export function PageVersionsDrawer({
  open,
  pageId,
  currentBody,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const { t } = useTranslation('pages');

  const { data } = useQuery({
    queryKey: ['page-versions', pageId],
    queryFn: () => pagesApi.listVersions(pageId),
    enabled: open,
  });

  // VersionDrawer keys versions by `id` (string). The BE exposes a per-page
  // `versionNumber` (int) for restore and a unique `id` (GUID). We use
  // `versionNumber` as the key sent to the restore endpoint but keep the
  // human label readable.
  const versions = useMemo<VersionEntry[]>(() => {
    const list = data ?? [];
    return [...list]
      .sort((a, b) => b.versionNumber - a.versionNumber)
      .map((v, idx) => ({
        id: String(v.versionNumber),
        label: t('versions.label', {
          version: v.versionNumber,
          note: v.changeNote ?? t('versions.defaultNote'),
        }),
        author: v.authorEmail ?? t('versions.systemAuthor'),
        createdAt: dayjs(v.createdAt).format('DD.MM.YYYY HH:mm'),
        snapshot: v.body,
        current: idx === 0,
      }));
  }, [data, t]);

  const restoreMutation = useMutation({
    mutationFn: (versionNumber: number) =>
      pagesApi.restoreVersion(pageId, versionNumber),
    onSuccess: async (_d, versionNumber) => {
      toast.success(t('toast.versionRestored', { version: versionNumber }));
      await qc.invalidateQueries({ queryKey: ['pages', pageId] });
      await qc.invalidateQueries({ queryKey: ['page-versions', pageId] });
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.version.restore',
        resource: 'page',
        resourceId: pageId,
        summary: t('audit.versionRestored', { version: versionNumber }),
        meta: { versionNumber },
      });
      onClose();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  return (
    <VersionDrawer
      open={open}
      onClose={onClose}
      versions={versions}
      currentContent={currentBody}
      onRestore={async (v) => {
        const num = Number(v.id);
        if (Number.isFinite(num)) await restoreMutation.mutateAsync(num);
      }}
    />
  );
}
